import { StandardEvents } from '@shopify/events';

export class BinderProductGallery extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.dialog = this.querySelector('dialog');
    this.image = this.querySelector('[data-gallery-image]');
    this.lens = this.querySelector('[data-gallery-lens]');
    this.wrap = this.querySelector('[data-gallery-image-wrap]');
    this.stage = this.querySelector('[data-gallery-stage]');
    this.images = [...this.querySelectorAll('[data-gallery-images] [data-src]')].map(node => ({ src: node.dataset.src, alt: node.dataset.alt }));
    this.index = 0;
    this.revision = 0;
    this.zoomed = false;
    this.querySelector('[data-gallery-open]').addEventListener('click', () => this.openViewer(), options);
    this.querySelectorAll('[data-gallery-photo-open]').forEach(button => {
      button.addEventListener('click', () => this.openViewer(Number(button.dataset.galleryPhotoOpen) || 0), options);
    });
    this.querySelector('[data-gallery-close]').addEventListener('click', () => this.dialog.close(), options);
    this.querySelector('[data-gallery-prev]').addEventListener('click', () => this.showImage(this.index - 1), options);
    this.querySelector('[data-gallery-next]').addEventListener('click', () => this.showImage(this.index + 1), options);
    this.querySelector('[data-gallery-zoom]').addEventListener('click', () => this.setZoom(!this.zoomed), options);
    this.dialog.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.showImage(this.index + (event.key === 'ArrowLeft' ? -1 : 1));
      }
    }, options);
    this.dialog.addEventListener('close', () => this.restorePage(), options);
    this.wrap.addEventListener('pointermove', event => this.moveLens(event), options);
    this.wrap.addEventListener('pointerleave', () => { this.lens.hidden = true; }, options);
    this.stage.addEventListener('pointerdown', event => {
      this.touchStart = event.pointerType === 'touch' && !this.zoomed ? { x: event.clientX, y: event.clientY } : null;
    }, options);
    this.stage.addEventListener('pointerup', event => {
      if (!this.touchStart || event.pointerType !== 'touch') return;
      const dx = event.clientX - this.touchStart.x;
      const dy = event.clientY - this.touchStart.y;
      this.touchStart = null;
      if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.5) this.showImage(this.index + (dx < 0 ? 1 : -1));
    }, options);
    this.stage.addEventListener('pointercancel', () => { this.touchStart = null; }, options);
    this.image.addEventListener('load', () => {
      this.image.dataset.loaded = 'true';
      this.querySelector('[data-gallery-error]').textContent = '';
      this.stage.setAttribute('aria-busy', 'false');
    }, options);
    this.image.addEventListener('error', () => {
      this.lens.hidden = true;
      this.stage.setAttribute('aria-busy', 'false');
      this.querySelector('[data-gallery-error]').textContent = 'This photo could not load. Try another photo or reopen the viewer.';
    }, options);
    window.addEventListener('resize', () => { this.lens.hidden = true; if (this.zoomed) this.setZoom(false); }, options);
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, event => {
      if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
      const revision = ++this.revision;
      event.promise?.then(({ detail }) => {
        if (!this.isConnected || revision !== this.revision || !detail?.html) return;
        const replacement = [...detail.html.querySelectorAll('bb-product-gallery')].find(gallery => gallery.dataset.bbGallerySection === this.dataset.bbGallerySection);
        if (replacement) this.replaceWith(replacement);
      }).catch(() => { /* Horizon reports product selection errors in the product form. */ });
    }, options);
  }

  disconnectedCallback() {
    if (this.dialog?.open) this.dialog.close();
    this.restorePage();
    this.controller?.abort();
    this.controller = null;
  }

  openViewer(index = 0) {
    if (!this.images.length || this.dialog.open) return;
    this.opener = document.activeElement;
    this.showImage(index);
    this.savedOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    try { this.dialog.showModal(); }
    catch { this.restorePage(); }
  }

  restorePage() {
    if (this.savedOverflow !== undefined) {
      document.documentElement.style.overflow = this.savedOverflow;
      this.savedOverflow = undefined;
    }
    this.lens && (this.lens.hidden = true);
    this.setZoom(false);
    if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
    this.opener = null;
  }

  showImage(index) {
    if (!this.images.length) return;
    this.setZoom(false);
    this.index = (index + this.images.length) % this.images.length;
    const photo = this.images[this.index];
    this.lens.hidden = true;
    this.image.dataset.loaded = 'false';
    this.stage.setAttribute('aria-busy', 'true');
    this.image.alt = photo.alt || `Photo ${this.index + 1}`;
    this.image.src = photo.src;
    if (this.image.complete && this.image.naturalWidth) {
      this.image.dataset.loaded = 'true';
      this.stage.setAttribute('aria-busy', 'false');
    }
    this.querySelector('[data-gallery-counter]').textContent = `${this.index + 1} / ${this.images.length}`;
    this.querySelector('[data-gallery-error]').textContent = '';
    for (const button of this.querySelectorAll('[data-gallery-prev], [data-gallery-next]')) button.disabled = this.images.length < 2;
    if (this.images.length > 1) {
      const preload = new Image();
      preload.src = this.images[(this.index + 1) % this.images.length].src;
    }
  }

  setZoom(zoomed) {
    if (!this.image || !this.dialog) return;
    const button = this.querySelector('[data-gallery-zoom]');
    const fitWidth = this.image.getBoundingClientRect().width;
    this.zoomed = Boolean(zoomed && this.image.naturalWidth && this.image.dataset.loaded === 'true');
    this.lens.hidden = true;
    this.dialog.classList.toggle('bb-viewer-zoomed', this.zoomed);
    this.image.style.width = this.zoomed ? `${Math.min(this.image.naturalWidth, fitWidth * 2.5)}px` : '';
    button.textContent = this.zoomed ? 'Fit to screen' : 'Zoom in';
    button.setAttribute('aria-pressed', String(this.zoomed));
    if (this.stage) { this.stage.scrollTop = 0; this.stage.scrollLeft = 0; }
  }

  moveLens(event) {
    if (event.pointerType === 'touch' || !this.dialog.open || this.zoomed || this.image.dataset.loaded !== 'true') { this.lens.hidden = true; return; }
    const rect = this.image.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    if (rect.width === 0 || x < 0 || y < 0 || x > rect.width || y > rect.height) { this.lens.hidden = true; return; }
    const zoom = Math.max(1.5, Math.min(5, Number(this.dataset.bbZoom) || 2.5));
    const size = 180;
    this.lens.hidden = false;
    this.lens.style.left = `${x - size / 2}px`;
    this.lens.style.top = `${y - size / 2}px`;
    this.lens.style.backgroundImage = `url(${JSON.stringify(this.image.currentSrc || this.image.src)})`;
    this.lens.style.backgroundSize = `${rect.width * zoom}px ${rect.height * zoom}px`;
    this.lens.style.backgroundPosition = `${size / 2 - x * zoom}px ${size / 2 - y * zoom}px`;
  }
}

if (!customElements.get('bb-product-gallery')) customElements.define('bb-product-gallery', BinderProductGallery);
